/* eslint-disable react/display-name */
import React, { useEffect, useState } from 'react';

import Select from '@atlaskit/select';
import Tooltip from '@atlaskit/tooltip';

import { Label } from '@qality/common-components';
import { ValueContainerWithOverflow } from '@qality/common-components/src/select/select-components/ValueContainerWithOverflow';
import { FEATURE_FLAG } from '@qality/common-config';
import { IssueStatus } from '@qality/common-model';

import { HEADING_AND_MESSAGES_STYLE } from './IssueStatusPicker.styled';
import {
  filterOutAllStatusesOption,
  getIssueStatusOptions,
  mapSelectOptionsToStatuses,
  mapStatusesToSelectOptions,
} from './IssueStatusPicker.util';

export type IssueStatusOption = {
  label: string;
  value: Array<IssueStatus>;
};

export type Props = {
  issueStatuses: Array<IssueStatus>;
  isIssueStatusesLoaded: boolean;
  selectedIssueStatuses: Array<IssueStatus>;
  onChange: (issueStatus: Array<IssueStatus>) => void;
  tooltipContent?: string;
  optionsLimit?: number;
};

export const ALL_ISSUE_STATUSES = {
  label: 'All statuses',
  value: null,
};

export default function IssueStatusPicker({
  issueStatuses,
  isIssueStatusesLoaded,
  selectedIssueStatuses,
  onChange,
  tooltipContent = '',
}: Props): JSX.Element {
  const [issueStatusOptions, setIssueStatusOptions] = useState<
    Array<IssueStatusOption>
  >(getIssueStatusOptions(issueStatuses));

  useEffect(() => {
    if (isIssueStatusesLoaded) {
      const newIssueStatusOptions = getIssueStatusOptions(issueStatuses);
      setIssueStatusOptions(newIssueStatusOptions);
    }
  }, [issueStatuses, isIssueStatusesLoaded]);

  const getAvailableToPickIssueStatusOptions = (): IssueStatusOption[] => {
    const selectedOptions = mapStatusesToSelectOptions(selectedIssueStatuses);
    const remainingAvailableOptions = issueStatusOptions.filter((option) => {
      return !selectedOptions.find(
        (selectedOption) => selectedOption.label === option.label
      );
    });

    return filterOutAllStatusesOption(remainingAvailableOptions);
  };

  const onIssueStatusOptionsChange = (options: Array<IssueStatusOption>) => {
    if (options.length === 0 || options[options.length - 1].value === null) {
      onChange(null);
    } else {
      const filteredStatusesOptions = filterOutAllStatusesOption(options);
      onChange(mapSelectOptionsToStatuses(filteredStatusesOptions));
    }
  };

  const getCurrentStatusValue = (): Array<IssueStatusOption> => {
    const currentlySelectedOptions = mapStatusesToSelectOptions(
      selectedIssueStatuses
    );
    return currentlySelectedOptions.length === 0
      ? [ALL_ISSUE_STATUSES]
      : currentlySelectedOptions;
  };

  return (
    <>
      <Label text='Status' />
      <Tooltip content={tooltipContent}>
        <Select
          isMulti
          id='issue-status-picker'
          defaultValue={ALL_ISSUE_STATUSES}
          value={getCurrentStatusValue()}
          options={getAvailableToPickIssueStatusOptions()}
          onChange={onIssueStatusOptionsChange}
          styles={{
            noOptionsMessage: HEADING_AND_MESSAGES_STYLE,
          }}
          isLoading={!isIssueStatusesLoaded}
          isDisabled={!FEATURE_FLAG.isPremium}
          components={{
            ValueContainer: ValueContainerWithOverflow,
          }}
        />
      </Tooltip>
    </>
  );
}
